import { BookOpen, Users, Clock, CheckCircle, PauseCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const SeriesCard = ({ series, onClick }) => {
  const statusConfig = {
    active: { icon: Clock, color: '#22C55E', bg: '#DCFCE7', label: 'Active' },
    completed: { icon: CheckCircle, color: '#3B82F6', bg: '#DBEAFE', label: 'Completed' },
    hiatus: { icon: PauseCircle, color: '#F59E0B', bg: '#FEF3C7', label: 'Hiatus' }
  };

  const status = statusConfig[series.status] || statusConfig.active;
  const StatusIcon = status.icon;

  return (
    <div
      onClick={onClick}
      className="rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] group dark:hover:shadow-lg"
      style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
    >
      {/* Cover Image */}
      <div
        className="relative aspect-[4/3] overflow-hidden"
        style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
      >
        {series.cover_image_url ? (
          <img
            src={getImageUrl(series.cover_image_url)}
            alt={series.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-8 h-8" style={{ color: 'var(--st-border-medium)' }} />
          </div>
        )}

        {/* Status Badge */}
        <div
          className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5"
          style={{ background: 'rgba(255, 255, 255, 0.95)', color: status.color }}
        >
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Title */}
        <h3
          className="font-bold text-sm mb-1 truncate"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          {series.title}
        </h3>

        {/* Description - only show on larger screens */}
        {series.description && (
          <p
            className="text-[11px] line-clamp-1 mb-2 hidden sm:block"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {series.description}
          </p>
        )}

        {/* Stats Row */}
        <div className="flex items-center gap-2 text-[11px]">
          {/* Book Count */}
          <div className="flex items-center gap-1">
            <BookOpen className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
            <span className="font-medium" style={{ color: 'var(--st-text-heading)' }}>
              {series.book_count || 0}
            </span>
          </div>

          {/* Character Count */}
          {series.characters?.length > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
              <span className="font-medium" style={{ color: 'var(--st-text-heading)' }}>
                {series.characters.length}
              </span>
            </div>
          )}

          {/* Age Range */}
          <span
            className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}
          >
            {series.age_range}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SeriesCard;
